import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { X, Sparkles } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gallery } from '../mockData';

gsap.registerPlugin(ScrollTrigger);

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current?.children || [], {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: { trigger: headerRef.current, start: 'top 80%' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!gridRef.current) return;
    const items = gridRef.current.querySelectorAll('.gallery-tile');
    gsap.fromTo(
      items,
      { y: 50, opacity: 0, scale: 0.95 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.7,
        stagger: 0.08,
        ease: 'power3.out',
      }
    );
  }, [filter]);

  const filteredGallery =
    filter === 'all' ? gallery : gallery.filter((item) => item.category === filter);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'salon', label: 'Salon' },
    { key: 'nail-art', label: 'Nail Art' },
    { key: 'service', label: 'Services' },
  ];

  return (
    <section
      id="gallery"
      ref={sectionRef}
      data-testid="gallery-section"
      className="py-24 bg-gradient-to-b from-pink-50/30 to-white relative overflow-hidden"
    >
      <div className="absolute top-1/4 -right-32 w-96 h-96 bg-fuchsia-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-rose-100 px-4 py-2 rounded-full mb-5">
            <Sparkles className="w-4 h-4 text-rose-600" />
            <span className="text-sm font-medium text-rose-700">Our Work</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 bg-gradient-to-r from-rose-500 to-pink-600 bg-clip-text text-transparent leading-tight">
            Nail Art Gallery
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Real designs created at our salon — pink neon glow, floral elegance, and every shade in between.
          </p>
        </div>

        <div className="flex justify-center flex-wrap gap-3 mb-12">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`gallery-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                filter === f.key
                  ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg shadow-rose-200 scale-105'
                  : 'bg-white text-gray-600 hover:bg-rose-50 border border-gray-200 hover:border-rose-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredGallery.map((item) => (
            <div
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="gallery-tile group cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              <Card className="overflow-hidden border-none shadow-md hover:shadow-2xl hover:shadow-pink-300/40 transition-all duration-500 rounded-2xl">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={item.url}
                    alt={item.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-pink-900/60 via-pink-900/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                    <p className="text-white font-medium text-sm">{item.alt}</p>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};
